import React, { useState, useEffect } from 'react';
import {
  Row, Col, Spin, Alert, List, Progress, Tag, Space,
  Button, Select, Table, Modal, Form, Input, Empty, Tabs, message, Badge
} from 'antd';
import {
  LineChartOutlined, WarningOutlined, BulbOutlined, CheckCircleOutlined,
  ExclamationCircleOutlined, SyncOutlined, DollarOutlined, TrophyOutlined,
  ArrowUpOutlined, ArrowDownOutlined, MinusOutlined, ReloadOutlined,
  RocketOutlined, BarChartOutlined, ThunderboltOutlined
} from '@ant-design/icons';
import ReactECharts from 'echarts-for-react';
import { forecastingService } from '../services/api';
import '../styles/Phase5.css';

const { Option } = Select;

const SpendForecasting = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [forecasts, setForecasts] = useState(null);
  const [anomalies, setAnomalies] = useState([]);
  const [budgetRecommendations, setBudgetRecommendations] = useState(null);
  const [modelAccuracy, setModelAccuracy] = useState([]);
  const [historical, setHistorical] = useState(null);

  const [forecastType, setForecastType] = useState('total');
  const [monthsAhead, setMonthsAhead] = useState(12);
  const [trainingModalVisible, setTrainingModalVisible] = useState(false);
  const [resolveModalVisible, setResolveModalVisible] = useState(false);
  const [selectedAnomaly, setSelectedAnomaly] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const currentYear = new Date().getFullYear();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [statsData, forecastsData, anomaliesData, histData] = await Promise.all([
        forecastingService.getStats().catch(() => null),
        forecastingService.getForecasts({
          months_ahead: monthsAhead,
          target_type: forecastType,
          include_confidence_intervals: true
        }).catch(() => null),
        forecastingService.getAnomalies({ limit: 20 }).catch(() => []),
        forecastingService.getHistoricalSpend({
          target_type: forecastType,
          lookback_months: 24
        }).catch(() => null),
      ]);

      setStats(statsData);
      setForecasts(forecastsData);
      setAnomalies(anomaliesData || []);
      setHistorical(histData);

      try {
        const budgetData = await forecastingService.getBudgetRecommendations({
          target_year: currentYear + 1,
          include_categories: true,
          include_total: true
        });
        setBudgetRecommendations(budgetData);
      } catch (e) {
        console.log('Budget recommendations not available');
      }

      try {
        const accuracy = await forecastingService.getAccuracy();
        setModelAccuracy(accuracy || []);
      } catch (e) {
        console.log('Model accuracy not available');
      }

    } catch (err) {
      console.error('Error fetching forecasting data:', err);
      setError('Failed to load forecasting data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleForecastTypeChange = async (value, newMonthsAhead = null) => {
    setForecastType(value);
    setLoading(true);
    const months = newMonthsAhead !== null ? newMonthsAhead : monthsAhead;
    try {
      const [forecastsData, histData] = await Promise.all([
        forecastingService.getForecasts({
          months_ahead: months,
          target_type: value,
          include_confidence_intervals: true
        }),
        forecastingService.getHistoricalSpend({
          target_type: value,
          lookback_months: 24
        }),
      ]);
      setForecasts(forecastsData);
      setHistorical(histData);
    } catch (err) {
      message.error('Failed to update forecasts');
    } finally {
      setLoading(false);
    }
  };

  const handleMonthsAheadChange = async (value) => {
    setMonthsAhead(value);
    handleForecastTypeChange(forecastType, value);
  };

  const handleTrainModel = async (values) => {
    setActionLoading(true);
    try {
      const result = await forecastingService.trainModel({
        model_type: values.model_type,
        target_type: values.target_type,
        lookback_months: values.lookback_months || 12
      });
      message.success(`Model trained successfully! MAPE: ${result.mape?.toFixed(2)}%`);
      setTrainingModalVisible(false);
      fetchData();
    } catch (err) {
      message.error(err.response?.data?.detail || 'Failed to train model');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDetectAnomalies = async () => {
    setActionLoading(true);
    try {
      const result = await forecastingService.detectAnomalies({
        lookback_months: 12,
        z_score_threshold: 2.0,
        include_vendors: true
      });
      message.success(`Detected ${result.anomalies_detected} anomalies`);
      const newAnomalies = await forecastingService.getAnomalies({ limit: 20 });
      setAnomalies(newAnomalies || []);
    } catch (err) {
      message.error('Failed to detect anomalies');
    } finally {
      setActionLoading(false);
    }
  };

  const handleResolveAnomaly = async (values) => {
    if (!selectedAnomaly) return;
    setActionLoading(true);
    try {
      await forecastingService.resolveAnomaly(selectedAnomaly.id, {
        status: values.status,
        resolution_notes: values.resolution_notes
      });
      message.success('Anomaly resolved');
      setResolveModalVisible(false);
      setSelectedAnomaly(null);
      const newAnomalies = await forecastingService.getAnomalies({ limit: 20 });
      setAnomalies(newAnomalies || []);
    } catch (err) {
      message.error('Failed to resolve anomaly');
    } finally {
      setActionLoading(false);
    }
  };

  // Enhanced Chart configuration with new design
  const getForecastChartOption = () => {
    if (!forecasts?.forecasts || forecasts.forecasts.length === 0) {
      return {};
    }

    const dates = forecasts.forecasts.map(f => f.date);
    const predicted = forecasts.forecasts.map(f => f.predicted_amount);
    const actual = forecasts.forecasts.map(f => f.actual_amount);
    const lowerBound = forecasts.forecasts.map(f => f.lower_bound);
    const upperBound = forecasts.forecasts.map(f => f.upper_bound);

    return {
      tooltip: {
        trigger: 'axis',
        backgroundColor: 'rgba(10, 22, 40, 0.95)',
        borderColor: 'rgba(245, 158, 11, 0.3)',
        borderWidth: 1,
        textStyle: {
          fontFamily: 'IBM Plex Sans, sans-serif',
          color: '#f1f5f9'
        },
        formatter: (params) => {
          let result = `<div style="font-weight: 600; margin-bottom: 8px;">${params[0].axisValue}</div>`;
          params.forEach(p => {
            if (p.value !== null && p.value !== undefined && p.seriesName !== 'Lower Bound') {
              const color = p.seriesName === 'Predicted' ? '#f59e0b' :
                           p.seriesName === 'Actual' ? '#10b981' : '#94a3b8';
              result += `<div style="margin: 4px 0;"><span style="color: ${color};">\u25CF</span> ${p.seriesName}: <strong>$${p.value.toLocaleString()}</strong></div>`;
            }
          });
          return result;
        }
      },
      legend: {
        data: ['Predicted', 'Actual'],
        bottom: 0,
        textStyle: {
          fontFamily: 'IBM Plex Sans, sans-serif',
          color: '#64748b'
        },
        itemStyle: {
          borderWidth: 0
        }
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '15%',
        top: '8%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: dates,
        axisLine: {
          lineStyle: { color: '#cbd5e1' }
        },
        axisLabel: {
          rotate: 45,
          fontFamily: 'IBM Plex Sans, sans-serif',
          color: '#64748b',
          fontSize: 11
        },
        splitLine: { show: false }
      },
      yAxis: {
        type: 'value',
        axisLine: { show: false },
        axisLabel: {
          fontFamily: 'JetBrains Mono, monospace',
          color: '#64748b',
          fontSize: 11,
          formatter: (value) => '$' + (value / 1000).toFixed(0) + 'k'
        },
        splitLine: {
          lineStyle: {
            color: '#f1f5f9',
            type: 'dashed'
          }
        }
      },
      series: [
        {
          name: 'Confidence Range',
          type: 'line',
          data: upperBound,
          lineStyle: { opacity: 0 },
          areaStyle: {
            color: {
              type: 'linear',
              x: 0, y: 0, x2: 0, y2: 1,
              colorStops: [
                { offset: 0, color: 'rgba(245, 158, 11, 0.15)' },
                { offset: 1, color: 'rgba(245, 158, 11, 0.02)' }
              ]
            }
          },
          stack: 'confidence',
          symbol: 'none',
          showInLegend: false
        },
        {
          name: 'Lower Bound',
          type: 'line',
          data: lowerBound,
          lineStyle: { opacity: 0 },
          areaStyle: { opacity: 0 },
          stack: 'confidence',
          symbol: 'none',
          showInLegend: false
        },
        {
          name: 'Predicted',
          type: 'line',
          data: predicted,
          smooth: 0.3,
          lineStyle: {
            color: '#f59e0b',
            width: 3,
            shadowColor: 'rgba(245, 158, 11, 0.3)',
            shadowBlur: 10,
            shadowOffsetY: 5
          },
          itemStyle: {
            color: '#f59e0b',
            borderWidth: 2,
            borderColor: '#fff'
          },
          symbol: 'circle',
          symbolSize: 8,
          emphasis: {
            itemStyle: {
              shadowColor: 'rgba(245, 158, 11, 0.5)',
              shadowBlur: 20
            }
          }
        },
        {
          name: 'Actual',
          type: 'line',
          data: actual,
          smooth: 0.3,
          lineStyle: {
            color: '#10b981',
            width: 2,
            type: [6, 4]
          },
          itemStyle: {
            color: '#10b981',
            borderWidth: 2,
            borderColor: '#fff'
          },
          symbol: 'circle',
          symbolSize: 6
        }
      ]
    };
  };

  const getSeverityTagClass = (severity) => {
    const classes = {
      critical: 'p5-tag--danger',
      high: 'p5-tag--warning',
      medium: 'p5-tag--warning',
      low: 'p5-tag--info'
    };
    return classes[severity] || 'p5-tag--neutral';
  };

  const getTrendIcon = (trend) => {
    if (trend === 'increasing') return <ArrowUpOutlined style={{ color: 'var(--p5-rose-500)' }} />;
    if (trend === 'decreasing') return <ArrowDownOutlined style={{ color: 'var(--p5-emerald-500)' }} />;
    return <MinusOutlined style={{ color: 'var(--p5-amber-500)' }} />;
  };

  const anomalyColumns = [
    {
      title: 'Period',
      key: 'period',
      width: 100,
      render: (_, record) => (
        <span className="p5-table-mono">{record.spend_date}</span>
      )
    },
    {
      title: 'Source',
      key: 'source',
      width: 180,
      render: (_, record) => (
        <div>
          {record.vendor_name && (
            <div style={{ fontWeight: 500, color: 'var(--p5-navy-800)' }}>
              {record.vendor_name}
            </div>
          )}
          {record.category && (
            <span className="p5-tag p5-tag--neutral" style={{ fontSize: '0.75rem' }}>
              {record.category}
            </span>
          )}
          {!record.vendor_name && !record.category && (
            <span style={{ color: 'var(--p5-slate-400)' }}>Total Spend</span>
          )}
        </div>
      )
    },
    {
      title: 'Type',
      dataIndex: 'anomaly_type',
      width: 90,
      render: (type) => (
        <span className={`p5-tag ${type === 'spike' ? 'p5-tag--danger' : 'p5-tag--info'}`}>
          {type?.toUpperCase()}
        </span>
      )
    },
    {
      title: 'Severity',
      dataIndex: 'severity',
      width: 100,
      render: (severity) => (
        <span className={`p5-tag ${getSeverityTagClass(severity)}`}>
          <span className="p5-tag-dot" />
          {severity?.toUpperCase()}
        </span>
      )
    },
    {
      title: 'Actual',
      dataIndex: 'actual_amount',
      width: 110,
      render: (val) => (
        <span className="p5-font-mono p5-table-primary">${val?.toLocaleString()}</span>
      )
    },
    {
      title: 'Expected',
      dataIndex: 'expected_amount',
      width: 110,
      render: (val) => (
        <span className="p5-font-mono" style={{ color: 'var(--p5-slate-500)' }}>
          ${val?.toLocaleString()}
        </span>
      )
    },
    {
      title: 'Deviation',
      dataIndex: 'deviation_percentage',
      width: 90,
      render: (val) => (
        <span style={{
          fontFamily: 'var(--p5-font-mono)',
          fontWeight: 600,
          color: val > 0 ? 'var(--p5-rose-500)' : 'var(--p5-emerald-500)'
        }}>
          {val > 0 ? '+' : ''}{val?.toFixed(1)}%
        </span>
      )
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 110,
      render: (status) => {
        const statusClass = {
          new: 'p5-tag--info',
          investigating: 'p5-tag--warning',
          resolved: 'p5-tag--success',
          dismissed: 'p5-tag--neutral'
        };
        return (
          <span className={`p5-tag ${statusClass[status] || 'p5-tag--neutral'}`}>
            {status?.toUpperCase()}
          </span>
        );
      }
    },
    {
      title: 'Action',
      key: 'action',
      width: 80,
      render: (_, record) => (
        record.status === 'new' || record.status === 'investigating' ? (
          <Button
            className="p5-btn p5-btn--ghost"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedAnomaly(record);
              setResolveModalVisible(true);
            }}
          >
            Resolve
          </Button>
        ) : null
      )
    }
  ];

  const tabItems = [
    {
      key: 'forecasts',
      label: (
        <span className="p5-flex p5-gap-sm">
          <LineChartOutlined />
          Forecasts
        </span>
      ),
      children: (
        <>
          {/* Forecast Chart */}
          <div className="p5-chart-container p5-animate-fadeUp" style={{ marginBottom: '24px' }}>
            <div className="p5-chart-title">
              <span>
                <BarChartOutlined style={{ color: 'var(--p5-amber-500)', marginRight: '8px' }} />
                Spend Forecast
              </span>
              <Space>
                <Select
                  value={forecastType}
                  onChange={handleForecastTypeChange}
                  style={{ width: 150 }}
                  size="middle"
                >
                  <Option value="total">Total Spend</Option>
                  <Option value="category">By Category</Option>
                  <Option value="vendor">By Vendor</Option>
                </Select>
                <Select
                  value={monthsAhead}
                  onChange={handleMonthsAheadChange}
                  style={{ width: 120 }}
                  size="middle"
                >
                  <Option value={6}>6 Months</Option>
                  <Option value={12}>12 Months</Option>
                  <Option value={24}>24 Months</Option>
                </Select>
              </Space>
            </div>
            {forecasts?.forecasts?.length > 0 ? (
              <>
                <ReactECharts
                  option={getForecastChartOption()}
                  style={{ height: 400 }}
                />
                <div className="p5-chart-legend">
                  <div className="p5-chart-legend-item">
                    <span className="p5-chart-legend-dot" style={{ background: 'var(--p5-amber-500)' }} />
                    Predicted Values
                  </div>
                  <div className="p5-chart-legend-item">
                    <span className="p5-chart-legend-dot" style={{ background: 'var(--p5-emerald-500)' }} />
                    Actual Values
                  </div>
                  <div className="p5-chart-legend-item">
                    <span className="p5-chart-legend-dot" style={{ background: 'rgba(245, 158, 11, 0.2)' }} />
                    Confidence Band
                  </div>
                </div>
              </>
            ) : (
              <div className="p5-empty">
                <LineChartOutlined className="p5-empty-icon" />
                <div className="p5-empty-title">No forecast data available</div>
                <div className="p5-empty-description">
                  Train a model to generate spend predictions and confidence intervals.
                </div>
                <Button
                  className="p5-btn p5-btn--accent p5-mt-lg"
                  icon={<TrophyOutlined />}
                  onClick={() => setTrainingModalVisible(true)}
                >
                  Train Model
                </Button>
              </div>
            )}
            {forecasts && forecasts.forecasts?.length > 0 && (
              <div style={{ marginTop: 16, textAlign: 'center', color: 'var(--p5-slate-400)', fontSize: '0.875rem' }}>
                Model: <span className="p5-font-mono">{forecasts.model_type}</span> |
                Generated: {new Date(forecasts.generated_at).toLocaleString()}
              </div>
            )}
          </div>

          {/* Model Accuracy */}
          {modelAccuracy.length > 0 && (
            <div className="p5-card p5-animate-fadeUp" style={{ animationDelay: '100ms' }}>
              <div className="p5-card-body">
                <h3 className="p5-section-title">
                  <TrophyOutlined /> Model Performance
                </h3>
                <Row gutter={[16, 16]}>
                  {modelAccuracy.map((model, idx) => (
                    <Col xs={24} md={8} key={model.model_id || idx}>
                      <div style={{
                        background: 'var(--p5-slate-100)',
                        borderRadius: 'var(--p5-radius-md)',
                        padding: '20px',
                        textAlign: 'center'
                      }}>
                        <div className="p5-label" style={{ marginBottom: '8px' }}>
                          {model.target_type?.toUpperCase()} - {model.model_type}
                        </div>
                        <div className="p5-font-display" style={{
                          fontSize: '2.25rem',
                          color: model.mape && model.mape < 15 ? 'var(--p5-emerald-500)' : 'var(--p5-amber-500)'
                        }}>
                          {model.mape?.toFixed(2) || 'N/A'}
                          <span style={{ fontSize: '1rem', color: 'var(--p5-slate-400)' }}>%</span>
                        </div>
                        <div className="p5-label" style={{ marginTop: '4px' }}>MAPE</div>
                        <div style={{ marginTop: '12px', fontSize: '0.8125rem', color: 'var(--p5-slate-400)' }}>
                          Training: {model.training_period}<br />
                          Samples: {model.training_samples}
                        </div>
                      </div>
                    </Col>
                  ))}
                </Row>
              </div>
            </div>
          )}
        </>
      ),
    },
    {
      key: 'anomalies',
      label: (
        <Badge count={stats?.unresolved_anomalies || 0} offset={[10, 0]}>
          <span className="p5-flex p5-gap-sm">
            <WarningOutlined />
            Anomalies
          </span>
        </Badge>
      ),
      children: (
        <div className="p5-table-wrapper p5-animate-fadeUp">
          {anomalies.length > 0 ? (
            <Table
              dataSource={anomalies}
              columns={anomalyColumns}
              rowKey="id"
              pagination={{ pageSize: 10 }}
              onRow={(record) => ({
                onClick: () => {
                  setSelectedAnomaly(record);
                  setResolveModalVisible(true);
                },
                style: { cursor: 'pointer' }
              })}
            />
          ) : (
            <div className="p5-empty">
              <ThunderboltOutlined className="p5-empty-icon" />
              <div className="p5-empty-title">No anomalies detected</div>
              <div className="p5-empty-description">
                Run anomaly detection to scan for spending irregularities and unexpected patterns.
              </div>
              <Button
                className="p5-btn p5-btn--primary p5-mt-lg"
                icon={<SyncOutlined />}
                onClick={handleDetectAnomalies}
                loading={actionLoading}
              >
                Detect Anomalies
              </Button>
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'budget',
      label: (
        <span className="p5-flex p5-gap-sm">
          <BulbOutlined />
          Budget Recommendations
        </span>
      ),
      children: (
        <div className="p5-animate-fadeUp">
          {budgetRecommendations ? (
            <>
              {budgetRecommendations.total_recommendation && (
                <div className="p5-card p5-card--featured" style={{ marginBottom: '24px' }}>
                  <div className="p5-card-body">
                    <div className="p5-flex-between" style={{ marginBottom: '24px' }}>
                      <div>
                        <div className="p5-label" style={{ color: 'var(--p5-slate-300)' }}>
                          Total Spend Recommendation
                        </div>
                        <h3 className="p5-font-display" style={{ fontSize: '1.5rem', color: '#fff', margin: '8px 0 0' }}>
                          FY {currentYear + 1} Budget
                        </h3>
                      </div>
                      <span className={`p5-tag ${
                        budgetRecommendations.total_recommendation.confidence === 'high' ? 'p5-tag--success' :
                        budgetRecommendations.total_recommendation.confidence === 'medium' ? 'p5-tag--warning' : 'p5-tag--danger'
                      }`} style={{ fontSize: '0.875rem', padding: '8px 16px' }}>
                        {budgetRecommendations.total_recommendation.confidence.toUpperCase()} CONFIDENCE
                      </span>
                    </div>
                    <Row gutter={32}>
                      <Col span={8}>
                        <div className="p5-label" style={{ color: 'var(--p5-slate-300)', marginBottom: '4px' }}>
                          Recommended Budget
                        </div>
                        <div className="p5-font-display" style={{ fontSize: '2.5rem', color: 'var(--p5-amber-400)' }}>
                          ${budgetRecommendations.total_recommendation.recommended_budget?.toLocaleString()}
                        </div>
                      </Col>
                      <Col span={8}>
                        <div className="p5-label" style={{ color: 'var(--p5-slate-300)', marginBottom: '4px' }}>
                          Historical Average
                        </div>
                        <div className="p5-font-display" style={{ fontSize: '2.5rem', color: '#fff' }}>
                          ${budgetRecommendations.total_recommendation.historical_average?.toLocaleString()}
                        </div>
                      </Col>
                      <Col span={8}>
                        <div className="p5-label" style={{ color: 'var(--p5-slate-300)', marginBottom: '4px' }}>
                          Trend
                        </div>
                        <div style={{ fontSize: '1.5rem', marginTop: '8px', color: '#fff' }}>
                          {getTrendIcon(budgetRecommendations.total_recommendation.historical_trend)}
                          <span style={{ marginLeft: '12px', textTransform: 'capitalize' }}>
                            {budgetRecommendations.total_recommendation.historical_trend}
                          </span>
                        </div>
                      </Col>
                    </Row>
                    <div style={{
                      marginTop: '24px',
                      paddingTop: '24px',
                      borderTop: '1px solid rgba(255,255,255,0.1)'
                    }}>
                      <p style={{ color: 'var(--p5-slate-200)', lineHeight: 1.6, margin: '0 0 12px' }}>
                        {budgetRecommendations.total_recommendation.rationale}
                      </p>
                      <span style={{ color: 'var(--p5-slate-400)', fontSize: '0.875rem' }}>
                        Range: ${budgetRecommendations.total_recommendation.lower_range?.toLocaleString()} -
                        ${budgetRecommendations.total_recommendation.upper_range?.toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {budgetRecommendations.category_recommendations?.length > 0 && (
                <div className="p5-card">
                  <div className="p5-card-body">
                    <h3 className="p5-section-title">
                      <DollarOutlined /> Category Recommendations
                    </h3>
                    <List
                      dataSource={budgetRecommendations.category_recommendations}
                      renderItem={(rec) => (
                        <List.Item style={{
                          padding: '16px',
                          background: 'var(--p5-slate-100)',
                          borderRadius: 'var(--p5-radius-md)',
                          marginBottom: '12px',
                          border: 'none'
                        }}>
                          <div style={{ flex: 1 }}>
                            <div className="p5-flex-between" style={{ marginBottom: '8px' }}>
                              <span style={{ fontWeight: 600, color: 'var(--p5-navy-800)', fontSize: '1rem' }}>
                                {rec.category}
                              </span>
                              <span className={`p5-tag ${
                                rec.confidence === 'high' ? 'p5-tag--success' :
                                rec.confidence === 'medium' ? 'p5-tag--warning' : 'p5-tag--danger'
                              }`}>
                                {rec.confidence}
                              </span>
                            </div>
                            <div className="p5-flex p5-gap-lg">
                              <span>
                                <span className="p5-text-slate">Recommended: </span>
                                <span className="p5-font-mono p5-text-amber" style={{ fontWeight: 600 }}>
                                  ${rec.recommended_budget?.toLocaleString()}
                                </span>
                              </span>
                              <span>
                                <span className="p5-text-slate">Historical: </span>
                                <span className="p5-font-mono">
                                  ${rec.historical_average?.toLocaleString()}
                                </span>
                              </span>
                              <span className="p5-flex p5-gap-sm">
                                {getTrendIcon(rec.historical_trend)}
                                <span style={{ textTransform: 'capitalize', color: 'var(--p5-slate-400)' }}>
                                  {rec.historical_trend}
                                </span>
                              </span>
                            </div>
                          </div>
                        </List.Item>
                      )}
                    />
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="p5-card">
              <div className="p5-empty">
                <BulbOutlined className="p5-empty-icon" />
                <div className="p5-empty-title">Budget recommendations unavailable</div>
                <div className="p5-empty-description">
                  Ensure you have sufficient historical spending data to generate AI-powered budget recommendations.
                </div>
              </div>
            </div>
          )}
        </div>
      ),
    },
  ];

  if (error) {
    return (
      <div className="p5-page">
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          style={{ borderRadius: 'var(--p5-radius-md)' }}
        />
      </div>
    );
  }

  return (
    <div className="p5-page">
      {/* Page Header */}
      <div className="p5-page-header p5-flex-between" style={{ alignItems: 'flex-start' }}>
        <div>
          <h1 className="p5-page-title">Spend Forecasting</h1>
          <p className="p5-page-subtitle">
            ML-powered predictions, anomaly detection, and intelligent budget recommendations
          </p>
        </div>
        <Space>
          <Button
            className="p5-btn p5-btn--ghost"
            icon={<SyncOutlined />}
            onClick={handleDetectAnomalies}
            loading={actionLoading}
          >
            Detect Anomalies
          </Button>
          <Button
            className="p5-btn p5-btn--accent"
            icon={<RocketOutlined />}
            onClick={() => setTrainingModalVisible(true)}
          >
            Train Model
          </Button>
          <Button
            className="p5-btn p5-btn--ghost"
            icon={<ReloadOutlined />}
            onClick={fetchData}
          >
            Refresh
          </Button>
        </Space>
      </div>

      <Spin spinning={loading}>
        {/* Stats Grid */}
        <div className="p5-stats-grid p5-stagger">
          <div className="p5-stat-card p5-animate-fadeUp">
            <div className="p5-stat-icon p5-stat-icon--amber">
              <TrophyOutlined />
            </div>
            <div className="p5-stat-label">Active Models</div>
            <div className="p5-stat-value">{stats?.active_models || 0}</div>
          </div>

          <div className="p5-stat-card p5-animate-fadeUp">
            <div className="p5-stat-icon p5-stat-icon--teal">
              <LineChartOutlined />
            </div>
            <div className="p5-stat-label">Total Forecasts</div>
            <div className="p5-stat-value">{stats?.total_forecasts || 0}</div>
          </div>

          <div className="p5-stat-card p5-animate-fadeUp">
            <div className="p5-stat-icon p5-stat-icon--rose">
              <WarningOutlined />
            </div>
            <div className="p5-stat-label">Unresolved Anomalies</div>
            <div className="p5-stat-value" style={{
              color: (stats?.unresolved_anomalies || 0) > 0 ? 'var(--p5-rose-500)' : undefined
            }}>
              {stats?.unresolved_anomalies || 0}
            </div>
          </div>

          <div className="p5-stat-card p5-animate-fadeUp">
            <div className="p5-stat-icon p5-stat-icon--violet">
              <CheckCircleOutlined />
            </div>
            <div className="p5-stat-label">Model Accuracy (MAPE)</div>
            <div className="p5-stat-value" style={{
              color: stats?.recent_accuracy && stats.recent_accuracy < 15 ? 'var(--p5-emerald-500)' : undefined
            }}>
              {stats?.recent_accuracy || 'N/A'}
              {stats?.recent_accuracy && <span style={{ fontSize: '1rem', color: 'var(--p5-slate-400)' }}>%</span>}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs
          defaultActiveKey="forecasts"
          items={tabItems}
          className="p5-tabs"
        />
      </Spin>

      {/* Train Model Modal */}
      <Modal
        title={null}
        open={trainingModalVisible}
        onCancel={() => setTrainingModalVisible(false)}
        footer={null}
        className="p5-modal"
      >
        <div style={{
          background: 'var(--p5-navy-900)',
          margin: '-24px -24px 24px',
          padding: '24px',
          borderRadius: 'var(--p5-radius-lg) var(--p5-radius-lg) 0 0'
        }}>
          <h2 className="p5-font-display" style={{ fontSize: '1.5rem', color: '#fff', margin: 0 }}>
            Train Forecast Model
          </h2>
        </div>
        <Form onFinish={handleTrainModel} layout="vertical">
          <Form.Item
            name="model_type"
            label={<span className="p5-label">Model Type</span>}
            initialValue="simple_average"
            rules={[{ required: true }]}
          >
            <Select size="large">
              <Option value="simple_average">Simple Average</Option>
              <Option value="linear_regression">Linear Regression</Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="target_type"
            label={<span className="p5-label">Target Type</span>}
            initialValue="total"
            rules={[{ required: true }]}
          >
            <Select size="large">
              <Option value="total">Total Spend</Option>
              <Option value="category">By Category</Option>
              <Option value="vendor">By Vendor</Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="lookback_months"
            label={<span className="p5-label">Training Data (Months)</span>}
            initialValue={12}
          >
            <Select size="large">
              <Option value={6}>6 Months</Option>
              <Option value={12}>12 Months</Option>
              <Option value={24}>24 Months</Option>
              <Option value={36}>36 Months</Option>
            </Select>
          </Form.Item>
          <Form.Item>
            <Button
              className="p5-btn p5-btn--accent"
              htmlType="submit"
              loading={actionLoading}
              block
              size="large"
              icon={<RocketOutlined />}
            >
              Train Model
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* Resolve Anomaly Modal */}
      <Modal
        title={null}
        open={resolveModalVisible}
        onCancel={() => {
          setResolveModalVisible(false);
          setSelectedAnomaly(null);
        }}
        footer={null}
        className="p5-modal"
      >
        <div style={{
          background: 'var(--p5-navy-900)',
          margin: '-24px -24px 24px',
          padding: '24px',
          borderRadius: 'var(--p5-radius-lg) var(--p5-radius-lg) 0 0'
        }}>
          <h2 className="p5-font-display" style={{ fontSize: '1.5rem', color: '#fff', margin: 0 }}>
            Resolve Anomaly
          </h2>
        </div>
        {selectedAnomaly && (
          <>
            {/* Anomaly Title and Source */}
            <div style={{
              background: 'var(--p5-slate-100)',
              borderRadius: 'var(--p5-radius-md)',
              padding: '16px',
              marginBottom: '16px'
            }}>
              {selectedAnomaly.title && (
                <div style={{ fontWeight: 600, fontSize: '1rem', color: 'var(--p5-navy-800)', marginBottom: '12px' }}>
                  {selectedAnomaly.title}
                </div>
              )}
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: selectedAnomaly.description ? '12px' : 0 }}>
                {selectedAnomaly.vendor_name && (
                  <span className="p5-tag p5-tag--info">
                    Vendor: {selectedAnomaly.vendor_name}
                  </span>
                )}
                {selectedAnomaly.category && (
                  <span className="p5-tag p5-tag--neutral">
                    Category: {selectedAnomaly.category}
                  </span>
                )}
                {!selectedAnomaly.vendor_name && !selectedAnomaly.category && (
                  <span className="p5-tag p5-tag--neutral">
                    Total Spend
                  </span>
                )}
                <span className={`p5-tag ${selectedAnomaly.anomaly_type === 'spike' ? 'p5-tag--danger' : 'p5-tag--info'}`}>
                  {selectedAnomaly.anomaly_type?.toUpperCase()}
                </span>
                <span className={`p5-tag ${getSeverityTagClass(selectedAnomaly.severity)}`}>
                  {selectedAnomaly.severity?.toUpperCase()}
                </span>
              </div>
              {selectedAnomaly.description && (
                <p style={{ color: 'var(--p5-navy-600)', marginBottom: 0, lineHeight: 1.5 }}>
                  {selectedAnomaly.description}
                </p>
              )}
            </div>

            {/* Key Metrics */}
            <div style={{
              background: 'var(--p5-slate-100)',
              borderRadius: 'var(--p5-radius-md)',
              padding: '16px',
              marginBottom: '24px'
            }}>
              <Row gutter={[16, 12]}>
                <Col span={8}>
                  <div className="p5-label">Period</div>
                  <div className="p5-font-mono" style={{ fontSize: '1.125rem', color: 'var(--p5-navy-800)' }}>
                    {selectedAnomaly.spend_date}
                  </div>
                </Col>
                <Col span={8}>
                  <div className="p5-label">Actual Amount</div>
                  <div className="p5-font-mono" style={{ fontSize: '1.125rem', color: 'var(--p5-navy-800)' }}>
                    ${selectedAnomaly.actual_amount?.toLocaleString()}
                  </div>
                </Col>
                <Col span={8}>
                  <div className="p5-label">Expected Amount</div>
                  <div className="p5-font-mono" style={{ fontSize: '1.125rem', color: 'var(--p5-slate-500)' }}>
                    ${selectedAnomaly.expected_amount?.toLocaleString()}
                  </div>
                </Col>
                <Col span={8}>
                  <div className="p5-label">Deviation</div>
                  <div className="p5-font-mono" style={{
                    fontSize: '1.125rem',
                    color: selectedAnomaly.deviation_percentage > 0 ? 'var(--p5-rose-500)' : 'var(--p5-emerald-500)'
                  }}>
                    {selectedAnomaly.deviation_percentage > 0 ? '+' : ''}{selectedAnomaly.deviation_percentage?.toFixed(1)}%
                  </div>
                </Col>
                <Col span={8}>
                  <div className="p5-label">Z-Score</div>
                  <div className="p5-font-mono" style={{ fontSize: '1.125rem', color: 'var(--p5-navy-800)' }}>
                    {selectedAnomaly.z_score?.toFixed(2) || 'N/A'}
                  </div>
                </Col>
                <Col span={8}>
                  <div className="p5-label">Detected</div>
                  <div style={{ fontSize: '0.875rem', color: 'var(--p5-navy-600)' }}>
                    {selectedAnomaly.created_at ? new Date(selectedAnomaly.created_at).toLocaleDateString() : 'N/A'}
                  </div>
                </Col>
              </Row>
            </div>

            <Form onFinish={handleResolveAnomaly} layout="vertical">
              <Form.Item
                name="status"
                label={<span className="p5-label">Resolution Status</span>}
                initialValue="resolved"
                rules={[{ required: true }]}
              >
                <Select size="large">
                  <Option value="resolved">Resolved - Issue addressed</Option>
                  <Option value="dismissed">Dismissed - False positive</Option>
                  <Option value="investigating">Investigating - Need more info</Option>
                </Select>
              </Form.Item>
              <Form.Item
                name="resolution_notes"
                label={<span className="p5-label">Resolution Notes</span>}
              >
                <Input.TextArea rows={3} placeholder="Explain the resolution..." size="large" />
              </Form.Item>
              <Form.Item>
                <Button
                  className="p5-btn p5-btn--accent"
                  htmlType="submit"
                  loading={actionLoading}
                  block
                  size="large"
                >
                  Submit Resolution
                </Button>
              </Form.Item>
            </Form>
          </>
        )}
      </Modal>
    </div>
  );
};

export default SpendForecasting;
